import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdminSession } from '../admin/AdminShell'
import { resolveApiBaseUrl } from '../../lib/api-base-url'
import { useEscapeClose } from '../../lib/use-escape-close'

type DashboardEvent = {
  id: string
  collaboratorId?: string | null
  collaboratorName: string
  spaceName: string
  type: 'checkin' | 'checkout' | 'break_start' | 'break_end' | 'unauthorized' | 'spoof_attempt' | 'delay' | 'absence'
  timestamp: string
  details: string
  punctualityStatus?: 'on_time' | 'within_tolerance' | 'late' | 'absent_after_tolerance' | 'not_applicable'
  minutesLate?: number
  expectedTime?: string | null
}

type DashboardData = {
  settings: {
    delayToleranceMinutes: number
    standardWorkdayMinutes: number
    defaultChartType: 'bar' | 'line'
    defaultViewMode: 'journeys' | 'day'
  }
  stats: {
    totalCollaborators: number
    activeToday: number
    absences: number
    delays: number
    toleranceAlerts?: number
    activeBreaks: number
    unauthorizedAttempts: number
    possibleSpoofing: number
    tolerance?: number
  }
  events: DashboardEvent[]
}

type DashboardPageProps = {
  session: AdminSession
}

type CollaboratorProfile = {
  id: string
  name: string
  email: string
  roleSlug: string
  status: string
  maxDailyJourneys: number
}

type CollaboratorProfileForm = {
  name: string
  email: string
  status: string
  maxDailyJourneys: number
}

type CollaboratorHistoryItem = {
  date: string
  time: string
  space: string
  type: 'checkin' | 'checkout' | 'break_start' | 'break_end' | 'unauthorized' | 'spoof_attempt' | 'delay' | 'absence'
  details: string
  punctualityStatus?: 'on_time' | 'within_tolerance' | 'late' | 'absent_after_tolerance' | 'not_applicable'
  minutesLate?: number
  expectedTime?: string | null
}

type CollaboratorHistoryData = {
  collaboratorId?: string | null
  name: string
  stats: {
    presences: number
    atrasos: number
    tolerancia?: number
    faltas?: number
    alertas: number
    espacos: number
  }
  events: CollaboratorHistoryItem[]
}

const apiBaseUrl = resolveApiBaseUrl()



function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}

function WarningIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}

function ShieldAlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  )
}

function CoffeeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
      <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
      <line x1="6" y1="1" x2="6" y2="4" />
      <line x1="10" y1="1" x2="10" y2="4" />
      <line x1="14" y1="1" x2="14" y2="4" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '12px', height: '12px', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '12px', height: '12px', marginLeft: '4px', display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function UserLinkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '11px', height: '11px', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

export function DashboardPage({ session }: DashboardPageProps) {
  const { t } = useTranslation()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterType, setFilterType] = useState<'all' | 'entry_exit' | 'breaks' | 'alerts'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSpace, setSelectedSpace] = useState('all')

  // Preferences
  const [chartType, setChartType] = useState<'bar' | 'line'>(
    'line'
  )
  const settingsAppliedRef = useRef(false)
  const liveStatsRequestId = useRef(0)
  const collaboratorHistoryRequestId = useRef(0)

  // Collaborator History Modal States
  const [collaboratorHistoryModal, setCollaboratorHistoryModal] = useState<CollaboratorHistoryData | null>(null)
  const [editingProfile, setEditingProfile] = useState<CollaboratorProfile | null>(null)
  const [profileForm, setProfileForm] = useState<CollaboratorProfileForm>({
    name: '',
    email: '',
    status: 'active',
    maxDailyJourneys: 1,
  })
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileFeedback, setProfileFeedback] = useState<string | null>(null)
  const [modalSearch, setModalSearch] = useState('')
  const [modalFilter, setModalFilter] = useState<string>('all')
  const [modalPage, setModalPage] = useState(1)
  const [activityPage, setActivityPage] = useState(1)

  const fetchLiveStats = async () => {
    const requestId = ++liveStatsRequestId.current

    try {
      const response = await fetch(`${apiBaseUrl}/dashboard/live`, {
        headers: {
          Authorization: `Bearer ${session.session.token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error('Falha ao obter dados dinâmicos do dashboard.')
      }

      const snapshot = (await response.json()) as DashboardData

      // Discard this response if a newer poll tick already started (or
      // resolved) while this request was in flight.
      if (requestId !== liveStatsRequestId.current) {
        return
      }

      setData(snapshot)
      if (!settingsAppliedRef.current) {
        setChartType(snapshot.settings.defaultChartType)
        settingsAppliedRef.current = true
      }
      setError(null)
    } catch (err) {
      if (requestId !== liveStatsRequestId.current) {
        return
      }

      setError(err instanceof Error ? err.message : 'Erro inesperado')
    } finally {
      if (requestId === liveStatsRequestId.current) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    void fetchLiveStats()

    const intervalId = setInterval(() => {
      void fetchLiveStats()
    }, 5000)

    return () => clearInterval(intervalId)
  }, [session.session.token])

  const stats = data?.stats || {
    totalCollaborators: 0,
    activeToday: 0,
    absences: 0,
    delays: 0,
    toleranceAlerts: 0,
    activeBreaks: 0,
    unauthorizedAttempts: 0,
    possibleSpoofing: 0,
  }

  const allEvents = data?.events || []
  const delayTolerance = data?.settings.delayToleranceMinutes || 0
  const availableSpaces = Array.from(new Set(allEvents.map((evt) => evt.spaceName))).sort()

  const toleranceAlerts = stats.toleranceAlerts || 0
  const onTimeArrivals = Math.max(0, stats.activeToday - stats.delays - toleranceAlerts)

  // Calculate today's overtime hours
  // Grouped by collaboratorId when available so two collaborators sharing the
  // same display name don't have their check-in/checkout events merged.
  const collabEvents = new Map<string, DashboardEvent[]>()
  allEvents.forEach((evt) => {
    const key = evt.collaboratorId || evt.collaboratorName
    collabEvents.set(key, [...(collabEvents.get(key) || []), evt])
  })

  let totalOvertimeMinutes = 0
  collabEvents.forEach((list) => {
    const sorted = [...list].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    let checkinTime: number | null = null
    let workedTodayMinutes = 0

    sorted.forEach((evt) => {
      if (evt.type === 'checkin' || evt.type === 'delay') {
        checkinTime = new Date(evt.timestamp).getTime()
      } else if (evt.type === 'checkout' && checkinTime !== null) {
        const diffMinutes = Math.round((new Date(evt.timestamp).getTime() - checkinTime) / 60000)
        workedTodayMinutes += diffMinutes
        checkinTime = null
      }
    })

    const standardWorkdayMinutes = data?.settings.standardWorkdayMinutes || 480
    if (workedTodayMinutes > standardWorkdayMinutes) {
      totalOvertimeMinutes += (workedTodayMinutes - standardWorkdayMinutes)
    }
  })

  const overtimeHours = parseFloat((totalOvertimeMinutes / 60).toFixed(1))
  const totalPunches = stats.activeToday || 0
  const suspectedFraud = (stats.possibleSpoofing || 0) + (stats.unauthorizedAttempts || 0)

  // Heights for the bar chart columns (maximum height 140px)
  const maxVal = Math.max(onTimeArrivals, stats.delays, stats.absences, suspectedFraud, overtimeHours, delayTolerance, 1)
  const onTimeHeight = Math.round((onTimeArrivals / maxVal) * 140)
  const delayHeight = Math.round((stats.delays / maxVal) * 140)
  const absenceHeight = Math.round((stats.absences / maxVal) * 140)
  const suspectHeight = Math.round((suspectedFraud / maxVal) * 140)
  const overtimeHeight = Math.round((overtimeHours / maxVal) * 140)
  const toleranceHeight = Math.round((delayTolerance / maxVal) * 140)

  // SVG Line Chart coordinates (viewbox: 0 0 500 180)
  const points = [
    { x: 40, y: 150 - onTimeHeight, label: 'No Horário', value: onTimeArrivals, color: '#10b981' },
    { x: 120, y: 150 - delayHeight, label: 'Atrasos', value: stats.delays, color: '#f59e0b' },
    { x: 200, y: 150 - absenceHeight, label: 'Faltas', value: stats.absences, color: '#ef4444' },
    { x: 280, y: 150 - suspectHeight, label: 'Suspeitas', value: suspectedFraud, color: '#ec4899' },
    { x: 360, y: 150 - overtimeHeight, label: 'H. Extras', value: `${overtimeHours}h`, color: '#7c3aed' },
    { x: 440, y: 150 - toleranceHeight, label: 'Tolerância', value: `${delayTolerance}m`, color: '#3b82f6' }
  ]
  const pathD = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`
  const areaD = `${pathD} L 440,150 L 40,150 Z`

  const filteredEvents = allEvents.filter((event) => {
    // 1. Filter by event category buttons
    if (filterType === 'entry_exit' && !(event.type === 'checkin' || event.type === 'checkout')) return false
    if (filterType === 'breaks' && !(event.type === 'break_start' || event.type === 'break_end')) return false
    if (filterType === 'alerts' && !(event.type === 'unauthorized' || event.type === 'spoof_attempt' || event.type === 'delay' || event.type === 'absence' || event.punctualityStatus === 'within_tolerance')) return false

    // 2. Filter by space
    if (selectedSpace !== 'all' && event.spaceName !== selectedSpace) return false

    // 3. Filter by search query (collaborator name or details)
    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase()
      const matchName = event.collaboratorName.toLowerCase().includes(q)
      const matchSpace = event.spaceName.toLowerCase().includes(q)
      const matchDetails = event.details.toLowerCase().includes(q)
      if (!matchName && !matchSpace && !matchDetails) return false
    }

    return true
  })
  const activityRowsPerPage = 10
  const activityTotalPages = Math.max(1, Math.ceil(filteredEvents.length / activityRowsPerPage))
  const activityStartRow = (activityPage - 1) * activityRowsPerPage
  const activityVisibleEvents = filteredEvents.slice(
    activityStartRow,
    activityStartRow + activityRowsPerPage,
  )

  useEffect(() => {
    setActivityPage(1)
  }, [filterType, searchQuery, selectedSpace])

  useEffect(() => {
    setActivityPage((current) => Math.min(Math.max(current, 1), activityTotalPages))
  }, [activityTotalPages])

  const formatEventTime = (isoString: string) => {
    try {
      return new Intl.DateTimeFormat('pt-BR', {
        timeStyle: 'medium',
      }).format(new Date(isoString))
    } catch {
      return '--:--:--'
    }
  }

  const getEventBadge = (type: string) => {
    switch (type) {
      case 'checkin':
        return <span className="admin-status admin-status--active">Entrada</span>
      case 'checkout':
        return <span className="admin-status admin-status--inactive">Saída</span>
      case 'break_start':
        return <span className="admin-status" style={{ backgroundColor: 'rgba(124, 58, 237, 0.12)', color: '#7c3aed' }}>Intervalo Início</span>
      case 'break_end':
        return <span className="admin-status" style={{ backgroundColor: 'rgba(124, 58, 237, 0.2)', color: '#6d28d9' }}>Intervalo Fim</span>
      case 'delay':
        return <span className="admin-status admin-status--warning">Atraso</span>
      case 'absence':
        return <span className="admin-status admin-status--error">Falta</span>
      case 'spoof_attempt':
        return <span className="admin-status admin-status--error" style={{ animation: 'pulse 2s infinite' }}>Fraude GPS</span>
      case 'unauthorized':
        return <span className="admin-status admin-status--error">Não Autorizado</span>
      default:
        return <span className="admin-status">{type}</span>
    }
  }

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'checkin':
      case 'checkout':
        return <CheckIcon />
      case 'break_start':
      case 'break_end':
        return <CoffeeIcon />
      case 'delay':
        return <ClockIcon />
      case 'absence':
        return <WarningIcon />
      case 'spoof_attempt':
        return <WarningIcon />
      case 'unauthorized':
        return <ShieldAlertIcon />
      default:
        return <ClockIcon />
    }
  }

  const handleOpenCollaboratorHistory = async (event: DashboardEvent) => {
    const requestId = ++collaboratorHistoryRequestId.current

    try {
      const idParam = event.collaboratorId ? `&collaboratorId=${encodeURIComponent(event.collaboratorId)}` : ''
      const response = await fetch(`${apiBaseUrl}/collaborators/history?name=${encodeURIComponent(event.collaboratorName)}${idParam}`, {
        headers: {
          Authorization: `Bearer ${session.session.token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error('Falha ao obter histórico do colaborador')
      }

      const historyData = await response.json() as CollaboratorHistoryData

      // If the user opened a different collaborator's history before this
      // request resolved, don't clobber it with this now-stale response.
      if (requestId !== collaboratorHistoryRequestId.current) {
        return
      }

      setCollaboratorHistoryModal({
        ...historyData,
        collaboratorId: event.collaboratorId || null,
      })
      setModalSearch('')
      setModalFilter('all')
      setModalPage(1)
    } catch (err) {
      if (requestId !== collaboratorHistoryRequestId.current) {
        return
      }

      setError(err instanceof Error ? err.message : 'Falha ao obter histórico do colaborador')
    }
  }

  const closeCollaboratorHistory = () => {
    setCollaboratorHistoryModal(null)
    setEditingProfile(null)
    setProfileFeedback(null)
  }

  const closeProfileOverlay = () => {
    setEditingProfile(null)
    setProfileFeedback(null)
  }

  useEscapeClose(Boolean(editingProfile || collaboratorHistoryModal), () => {
    if (editingProfile) {
      closeProfileOverlay()
      return
    }

    closeCollaboratorHistory()
  })

  if (loading && !data) {
    return (
      <div className="admin-loading-state">
        <p>{t('common.loading')}</p>
      </div>
    )
  }

  const handleEditCollaboratorProfile = async (collaboratorId: string) => {
    setProfileFeedback(null)

    try {
      const response = await fetch(`${apiBaseUrl}/collaborators/${collaboratorId}`, {
        headers: {
          Authorization: `Bearer ${session.session.token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error('Falha ao carregar perfil do colaborador.')
      }

      const payload = (await response.json()) as { collaborator: CollaboratorProfile }
      setEditingProfile(payload.collaborator)
      setProfileForm({
        name: payload.collaborator.name,
        email: payload.collaborator.email,
        status: payload.collaborator.status,
        maxDailyJourneys: Number(payload.collaborator.maxDailyJourneys) || 1,
      })
    } catch (err) {
      setProfileFeedback(err instanceof Error ? err.message : 'Falha ao carregar perfil.')
    }
  }

  const handleSaveCollaboratorProfile = async () => {
    if (!editingProfile) {
      return
    }

    setProfileSaving(true)
    setProfileFeedback(null)

    try {
      const response = await fetch(`${apiBaseUrl}/collaborators/${editingProfile.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${session.session.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: profileForm.name,
          email: profileForm.email,
          roleSlug: editingProfile.roleSlug,
          status: profileForm.status,
          maxDailyJourneys: Math.max(1, Math.floor(Number(profileForm.maxDailyJourneys) || 1)),
        }),
      })

      const payload = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error(payload?.message || 'Falha ao salvar perfil do colaborador.')
      }

      const collaborator = payload.collaborator as CollaboratorProfile
      setEditingProfile(collaborator)
      setProfileForm({
        name: collaborator.name,
        email: collaborator.email,
        status: collaborator.status,
        maxDailyJourneys: Number(collaborator.maxDailyJourneys) || 1,
      })
      setCollaboratorHistoryModal((current) =>
        current
          ? {
              ...current,
              name: collaborator.name,
            }
          : current,
      )
      setProfileFeedback('Perfil atualizado.')
      void fetchLiveStats()
    } catch (err) {
      setProfileFeedback(err instanceof Error ? err.message : 'Falha ao salvar perfil.')
    } finally {
      setProfileSaving(false)
    }
  }


  // Modal Table pagination and filtering
  const modalAllEvents = collaboratorHistoryModal?.events || []
  const modalFilteredEvents = modalAllEvents.filter((evt) => {
    const matchesSearch = evt.space.toLowerCase().includes(modalSearch.toLowerCase())
    if (!matchesSearch) return false

    if (modalFilter === 'all') return true
    if (modalFilter === 'checkin') return evt.type === 'checkin'
    if (modalFilter === 'checkout') return evt.type === 'checkout'
    if (modalFilter === 'break') return evt.type === 'break_start' || evt.type === 'break_end'
    if (modalFilter === 'alert') return evt.type === 'unauthorized' || evt.type === 'spoof_attempt' || evt.type === 'delay' || evt.type === 'absence' || evt.punctualityStatus === 'within_tolerance'
    return true
  })

  const modalRowsPerPage = 5
  const modalTotalPages = Math.ceil(modalFilteredEvents.length / modalRowsPerPage)
  const modalStartRow = (modalPage - 1) * modalRowsPerPage
  const modalVisibleEvents = modalFilteredEvents.slice(modalStartRow, modalStartRow + modalRowsPerPage)

  return (
    <div className="admin-content-shell admin-content-shell--full">
      <style>{`
        @keyframes livePulse {
          0% {
            transform: scale(0.9);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.8);
            opacity: 0.7;
          }
          50% {
            transform: scale(1.25);
            box-shadow: 0 0 0 12px rgba(16, 185, 129, 0);
            opacity: 1;
          }
          100% {
            transform: scale(0.9);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
            opacity: 0.7;
          }
        }
        .live-pulse-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #10b981;
          animation: livePulse 1.8s infinite ease-in-out;
        }
        .dashboard-header-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-left: auto;
          padding: 6px 12px;
          border-radius: 5px;
          background-color: rgba(16, 185, 129, 0.08);
          border: 1px solid rgba(16, 185, 129, 0.16);
          font-size: 0.78rem;
          font-weight: 800;
          color: #047857;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
      `}</style>

      <header className="admin-content-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="admin-content-header__icon">
            <ClockIcon />
          </div>
          <div className="admin-content-header__copy">
            <h2>Painel Operacional (Tempo Real)</h2>
            <p>Monitoramento ativo do registro de ponto dos colaboradores</p>
          </div>
        </div>

        <div className="dashboard-header-badge">
          <span className="live-pulse-dot" />
          <span>Monitoramento Ativo</span>
        </div>
      </header>

      {error ? (
        <div className="admin-toast admin-toast--error" style={{ margin: '0 0 20px' }}>
          <strong>{error}</strong>
        </div>
      ) : null}

      <section className="admin-panel" style={{ border: '0', background: 'transparent', boxShadow: 'none', padding: '0' }}>
        <div className="admin-stat-grid" style={{ marginBottom: '24px', gap: '12px' }}>
          <article className="admin-stat admin-stat--dashboard-card" style={{ borderLeft: '4px solid #10b981' }}>
            <div className="admin-card__icon" style={{ color: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
              <CheckIcon />
            </div>
            <span>Presentes Hoje</span>
            <strong>{stats.activeToday} <small style={{ fontSize: '0.85rem', color: 'var(--muted)', fontWeight: 500 }}>/ {stats.totalCollaborators}</small></strong>
            <small>Colaboradores ativos em campo</small>
          </article>

          <article className="admin-stat admin-stat--dashboard-card" style={{ borderLeft: '4px solid #f59e0b' }}>
            <div className="admin-card__icon" style={{ color: '#f59e0b', backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
              <ClockIcon />
            </div>
            <span>Atrasos</span>
            <strong style={{ color: '#d97706' }}>{stats.delays}</strong>
            <small>Após {delayTolerance} min de tolerância</small>
          </article>

          <article className="admin-stat admin-stat--dashboard-card" style={{ borderLeft: '4px solid #ef4444', background: stats.absences > 0 ? 'rgba(239, 68, 68, 0.06)' : undefined }}>
            <div className="admin-card__icon" style={{ color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
              <WarningIcon />
            </div>
            <span style={{ color: '#b91c1c' }}>Faltas agora</span>
            <strong style={{ color: '#ef4444' }}>{stats.absences}</strong>
            <small style={{ color: '#b91c1c' }}>Sem entrada após o limite</small>
          </article>

          <article className="admin-stat admin-stat--dashboard-card" style={{ borderLeft: '4px solid #f97316', background: toleranceAlerts > 0 ? 'rgba(249, 115, 22, 0.07)' : undefined }}>
            <div className="admin-card__icon" style={{ color: '#f97316', backgroundColor: 'rgba(249, 115, 22, 0.12)' }}>
              <ClockIcon />
            </div>
            <span style={{ color: '#c2410c' }}>Dentro da tolerância</span>
            <strong style={{ color: '#ea580c' }}>{toleranceAlerts}</strong>
            <small style={{ color: '#9a3412' }}>Chegaram atrasados, mas ainda contam presença</small>
          </article>
        </div>

        {/* Gráfico de Assiduidade e Horas Extras */}
        <div className="admin-panel" style={{ padding: '24px', backgroundColor: 'var(--surface)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Gráfico de Assiduidade e Horas Extras</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '1px', border: '1px solid var(--line)', borderRadius: '6px', overflow: 'hidden', backgroundColor: 'var(--bg-alt)' }}>
                <button
                  type="button"
                  onClick={() => setChartType('bar')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.74rem',
                    fontWeight: 600,
                    border: '0',
                    backgroundColor: chartType === 'bar' ? 'var(--brand-purple)' : 'transparent',
                    color: chartType === 'bar' ? '#fff' : 'var(--muted)',
                    cursor: 'pointer'
                  }}
                >
                  Barras
                </button>
                <button
                  type="button"
                  onClick={() => setChartType('line')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.74rem',
                    fontWeight: 600,
                    border: '0',
                    backgroundColor: chartType === 'line' ? 'var(--brand-purple)' : 'transparent',
                    color: chartType === 'line' ? '#fff' : 'var(--muted)',
                    cursor: 'pointer'
                  }}
                >
                  Linha
                </button>
              </div>
              <span style={{ fontSize: '0.78rem', padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--bg-alt)', color: 'var(--muted)', fontWeight: 500, border: '1px solid var(--line)' }}>
                Tolerância: {delayTolerance} min
              </span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '32px', alignItems: 'center' }}>
            {chartType === 'bar' ? (
              /* Gráfico de Barras SVG/CSS */
              <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: '180px', padding: '16px 8px 8px', borderBottom: '2px solid var(--line)', position: 'relative' }}>
                {/* Chegadas no Horário */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${onTimeHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#10b981', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>No Horário</span>
                </div>

                {/* Atrasos */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${delayHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#f59e0b', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>Atrasos</span>
                </div>

                {/* Faltas */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${absenceHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#ef4444', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>Faltas</span>
                </div>

                {/* Suspeitas */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${suspectHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#ec4899', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(236, 72, 153, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>Suspeitas</span>
                </div>

                {/* Horas Extras */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${overtimeHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#7c3aed', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>H. Extras</span>
                </div>

                {/* Tolerância */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', width: '52px' }}>
                  <div style={{ 
                    height: `${toleranceHeight}px`, 
                    width: '28px', 
                    backgroundColor: '#3b82f6', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
                  }} />
                  <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>Tolerância</span>
                </div>
              </div>
            ) : (
              /* Gráfico de Linha / Área SVG */
              <div style={{ height: '180px', padding: '16px 8px 8px', borderBottom: '2px solid var(--line)', position: 'relative' }}>
                <svg viewBox="0 0 500 180" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--brand-purple)" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="var(--brand-purple)" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>
                  
                  {/* Grid Lines */}
                  <line x1="40" y1="150" x2="440" y2="150" stroke="var(--line)" strokeWidth="1" strokeDasharray="4 4" />
                  <line x1="40" y1="80" x2="440" y2="80" stroke="var(--line)" strokeWidth="1" strokeDasharray="4 4" />
                  <line x1="40" y1="10" x2="440" y2="10" stroke="var(--line)" strokeWidth="1" strokeDasharray="4 4" />

                  {/* Filled Area */}
                  <path d={areaD} fill="url(#chartGradient)" />

                  {/* Smooth Line */}
                  <path d={pathD} fill="none" stroke="var(--brand-purple)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />

                  {/* Points circles and labels */}
                  {points.map((p, i) => (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r="6" fill="#fff" stroke={p.color} strokeWidth="3" style={{ cursor: 'pointer', transition: 'all 0.2s' }} />
                      <text x={p.x} y={p.y - 12} textAnchor="middle" fontSize="10" fontWeight="bold" fill="var(--text)">
                        {p.value}
                      </text>
                      <text x={p.x} y="170" textAnchor="middle" fontSize="10" fontWeight="600" fill="var(--muted)">
                        {p.label}
                      </text>
                    </g>
                  ))}
                </svg>
              </div>
            )}

            {/* Cartões de Métricas Detalhadas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px' }}>
              {/* No Horário */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Chegadas no Horário</div>
                <strong style={{ fontSize: '1.15rem', color: '#10b981' }}>{onTimeArrivals}</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${totalPunches > 0 ? (onTimeArrivals / totalPunches) * 100 : 0}%`, backgroundColor: '#10b981' }} />
                </div>
              </div>

              {/* Atrasos */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Atrasos</div>
                <strong style={{ fontSize: '1.15rem', color: '#f59e0b' }}>{stats.delays}</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${totalPunches > 0 ? (stats.delays / totalPunches) * 100 : 0}%`, backgroundColor: '#f59e0b' }} />
                </div>
              </div>

              {/* Faltas */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Ausências / Faltas</div>
                <strong style={{ fontSize: '1.15rem', color: '#ef4444' }}>{stats.absences}</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${stats.totalCollaborators > 0 ? (stats.absences / stats.totalCollaborators) * 100 : 0}%`, backgroundColor: '#ef4444' }} />
                </div>
              </div>

              {/* Suspeitas */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Suspeitas / Alertas</div>
                <strong style={{ fontSize: '1.15rem', color: '#ec4899' }}>{suspectedFraud}</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(236, 72, 153, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${totalPunches > 0 ? (suspectedFraud / totalPunches) * 100 : 0}%`, backgroundColor: '#ec4899' }} />
                </div>
              </div>

              {/* Horas Extras */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Horas Extras</div>
                <strong style={{ fontSize: '1.15rem', color: '#7c3aed' }}>{overtimeHours}h</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(124, 58, 237, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${overtimeHours > 0 ? Math.min(100, (overtimeHours / 8) * 100) : 0}%`, backgroundColor: '#7c3aed' }} />
                </div>
              </div>

              {/* Tolerância */}
              <div style={{ padding: '8px', border: '1px solid var(--line)', borderRadius: '6px', backgroundColor: 'var(--bg-alt)' }}>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '2px' }}>Tolerância Padrão</div>
                <strong style={{ fontSize: '1.15rem', color: '#3b82f6' }}>{delayTolerance}m</strong>
                <div style={{ height: '4px', width: '100%', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: '100%', backgroundColor: '#3b82f6' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-panel admin-panel--table-card" style={{ padding: '24px', backgroundColor: 'var(--surface)' }}>
          <div className="admin-panel__header" style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: 0 }}>Registro de Atividades</h3>
            <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: '0.82rem' }}>Atualização em tempo real das marcações e violações das regras do espaço</p>
          </div>

          {/* Barra de Filtros e Busca do Dashboard */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', backgroundColor: 'var(--bg-alt)', padding: '12px', borderRadius: '5px', border: '1px solid var(--line)', marginBottom: '16px' }}>
            <div style={{ flex: 1, minWidth: '240px' }}>
              <input
                type="search"
                placeholder="Buscar por colaborador ou detalhe..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--line)',
                  borderRadius: '5px',
                  fontSize: '0.85rem'
                }}
              />
            </div>
            <div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--line)',
                  borderRadius: '5px',
                  fontSize: '0.85rem',
                  backgroundColor: '#fff',
                  minWidth: '180px'
                }}
              >
                <option value="all">Todos os eventos</option>
                <option value="entry_exit">Entradas & Saídas</option>
                <option value="breaks">Intervalos</option>
                <option value="alerts">Alertas & Suspeitos</option>
              </select>
            </div>
            <div>
              <select
                value={selectedSpace}
                onChange={(e) => setSelectedSpace(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--line)',
                  borderRadius: '5px',
                  fontSize: '0.85rem',
                  backgroundColor: '#fff',
                  minWidth: '200px'
                }}
              >
                <option value="all">Todos os espaços</option>
                {availableSpaces.map((space) => (
                  <option key={space} value={space}>{space}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>Horário</th>
                  <th style={{ width: '240px' }}>Colaborador</th>
                  <th style={{ width: '220px' }}>Espaço</th>
                  <th style={{ width: '150px' }}>Evento</th>
                  <th style={{ minWidth: '320px' }}>Detalhes operacionais</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '36px 0' }}>
                      <div className="admin-empty-state">
                        <strong>Nenhuma atividade encontrada</strong>
                        <p>Nenhum registro corresponde aos filtros selecionados nas últimas horas.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  activityVisibleEvents.map((event) => {
                    const isCriticalAttendance = event.type === 'absence' || event.type === 'delay'
                    const isToleranceWarning = event.punctualityStatus === 'within_tolerance'
                    return (
                    <tr key={event.id} style={{
                      backgroundColor: event.type === 'spoof_attempt' || isCriticalAttendance
                        ? 'rgba(239, 68, 68, 0.05)'
                        : isToleranceWarning
                          ? 'rgba(249, 115, 22, 0.07)'
                          : undefined,
                      borderLeft: event.type === 'spoof_attempt' || isCriticalAttendance
                        ? '3px solid #ef4444'
                        : isToleranceWarning
                          ? '3px solid #f97316'
                          : undefined
                    }}>
                      <td>
                        <strong>{formatEventTime(event.timestamp)}</strong>
                      </td>
                      <td>
                        <div style={{ display: 'grid', gap: '4px' }}>
                          <button
                            type="button"
                            className="admin-collaborator-link"
                            onClick={() => handleOpenCollaboratorHistory(event)}
                          >
                            <UserLinkIcon />
                            {event.collaboratorName}
                          </button>
                        </div>
                      </td>
                      <td>
                        <span>{event.spaceName}</span>
                      </td>
                      <td>
                        {getEventBadge(event.type)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: event.type === 'spoof_attempt' || isCriticalAttendance ? '#b91c1c' : isToleranceWarning ? '#c2410c' : 'var(--muted)', fontSize: '0.82rem', fontWeight: isCriticalAttendance || isToleranceWarning ? 700 : 500 }}>
                          <span style={{ width: '16px', height: '16px', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                            {getEventIcon(event.type)}
                          </span>
                          <span>{event.details}</span>
                        </div>
                      </td>
                    </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="admin-pagination" aria-label="Paginação das atividades">
            <div className="admin-pagination__summary">
              <span>Página {activityPage} de {activityTotalPages}</span>
              <strong>
                {filteredEvents.length === 0
                  ? '0 de 0 itens'
                  : `${Math.min(activityStartRow + activityVisibleEvents.length, filteredEvents.length)} de ${filteredEvents.length} itens`}
              </strong>
            </div>
            <div className="admin-pagination__nav">
              <div className="admin-pagination__actions">
                <button
                  type="button"
                  className="admin-link-button"
                  disabled={activityPage === 1}
                  onClick={() => setActivityPage((current) => Math.max(current - 1, 1))}
                >
                  Anterior
                </button>
              </div>
              <div className="admin-pagination__pages" aria-label="Páginas das atividades">
                {Array.from({ length: activityTotalPages }, (_, index) => index + 1).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`admin-pagination__page${activityPage === page ? ' admin-pagination__page--active' : ''}`}
                    aria-current={activityPage === page ? 'page' : undefined}
                    onClick={() => setActivityPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <div className="admin-pagination__actions admin-pagination__actions--end">
                <button
                  type="button"
                  className="admin-link-button"
                  disabled={activityPage === activityTotalPages}
                  onClick={() => setActivityPage((current) => Math.min(current + 1, activityTotalPages))}
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Histórico do Colaborador Modal */}
      {collaboratorHistoryModal ? (
        <div className="admin-modal" role="dialog" aria-modal="true" aria-label={`Histórico do Colaborador: ${collaboratorHistoryModal.name}`}>
          <div className="admin-modal__surface admin-modal__surface--view" style={{ width: 'min(980px, calc(100vw - 32px))' }}>
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              aria-label={t('common.cancel')}
              onClick={closeCollaboratorHistory}
            >
              <XIcon />
            </button>

            <div className="admin-modal__view" style={{ display: 'grid', gap: '20px', padding: '24px' }}>
              <div className="admin-modal__view-hero" style={{ paddingBottom: '12px', borderBottom: '1px solid var(--line)' }}>
                <p className="admin-modal__eyebrow">Histórico de Atividades</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <h3 style={{ fontSize: '1.45rem', fontWeight: 800, color: 'var(--brand-purple)', margin: '4px 0 0' }}>
                    {collaboratorHistoryModal.name}
                  </h3>
                  {collaboratorHistoryModal.collaboratorId ? (
                    <button
                      type="button"
                      className="admin-action-button"
                      onClick={() => void handleEditCollaboratorProfile(collaboratorHistoryModal.collaboratorId as string)}
                      style={{ minWidth: '130px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                      <UserLinkIcon />
                      Editar perfil
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Métricas do Colaborador (Widgets) */}
              <div className="admin-stat-grid" style={{ gap: '12px' }}>
                <article className="admin-stat" style={{ padding: '14px', borderRadius: '5px', backgroundColor: 'rgba(16, 185, 129, 0.05)', borderLeft: '3px solid #10b981' }}>
                  <span style={{ fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', margin: 0 }}>Presenças</span>
                  <strong style={{ fontSize: '1.3rem', color: '#10b981', marginTop: '4px', display: 'block' }}>{collaboratorHistoryModal.stats.presences}</strong>
                </article>
                <article className="admin-stat" style={{ padding: '14px', borderRadius: '5px', backgroundColor: 'rgba(245, 158, 11, 0.05)', borderLeft: '3px solid #f59e0b' }}>
                  <span style={{ fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', margin: 0 }}>Atrasos</span>
                  <strong style={{ fontSize: '1.3rem', color: '#d97706', marginTop: '4px', display: 'block' }}>{collaboratorHistoryModal.stats.atrasos}</strong>
                </article>
                <article className="admin-stat" style={{ padding: '14px', borderRadius: '5px', backgroundColor: 'rgba(249, 115, 22, 0.07)', borderLeft: '3px solid #f97316' }}>
                  <span style={{ fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: '#9a3412', margin: 0 }}>Na tolerância</span>
                  <strong style={{ fontSize: '1.3rem', color: '#ea580c', marginTop: '4px', display: 'block' }}>{collaboratorHistoryModal.stats.tolerancia || 0}</strong>
                </article>
                <article className="admin-stat" style={{ padding: '14px', borderRadius: '5px', backgroundColor: 'rgba(239, 68, 68, 0.07)', borderLeft: '3px solid #ef4444' }}>
                  <span style={{ fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: '#b91c1c', margin: 0 }}>Faltas</span>
                  <strong style={{ fontSize: '1.3rem', color: '#dc2626', marginTop: '4px', display: 'block' }}>{collaboratorHistoryModal.stats.faltas || 0}</strong>
                </article>
                <article className="admin-stat" style={{ padding: '14px', borderRadius: '5px', backgroundColor: 'rgba(239, 68, 68, 0.05)', borderLeft: '3px solid #ef4444' }}>
                  <span style={{ fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', margin: 0 }}>Alertas</span>
                  <strong style={{ fontSize: '1.3rem', color: '#ef4444', marginTop: '4px', display: 'block' }}>{collaboratorHistoryModal.stats.alertas}</strong>
                </article>
                <article className="admin-stat" style={{ padding: '14px', borderRadius: '5px', backgroundColor: 'rgba(124, 58, 237, 0.05)', borderLeft: '3px solid #7c3aed' }}>
                  <span style={{ fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: 700, color: 'var(--muted)', margin: 0 }}>Espaços Vinculados</span>
                  <strong style={{ fontSize: '1.3rem', color: '#7c3aed', marginTop: '4px', display: 'block' }}>{collaboratorHistoryModal.stats.espacos}</strong>
                </article>
              </div>

              {/* Filtros e Busca */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', backgroundColor: 'var(--bg-alt)', padding: '12px', borderRadius: '5px', border: '1px solid var(--line)' }}>
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <input
                    type="search"
                    placeholder="Buscar por espaço..."
                    value={modalSearch}
                    onChange={(e) => {
                      setModalSearch(e.target.value)
                      setModalPage(1)
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--line)',
                      borderRadius: '5px',
                      fontSize: '0.85rem'
                    }}
                  />
                </div>
                <div>
                  <select
                    value={modalFilter}
                    onChange={(e) => {
                      setModalFilter(e.target.value)
                      setModalPage(1)
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid var(--line)',
                      borderRadius: '5px',
                      fontSize: '0.85rem',
                      backgroundColor: '#fff',
                      minWidth: '180px'
                    }}
                  >
                    <option value="all">Todos os eventos</option>
                    <option value="checkin">Entradas</option>
                    <option value="checkout">Saídas</option>
                    <option value="break">Intervalos</option>
                    <option value="alert">Alertas & Suspeitos</option>
                  </select>
                </div>
              </div>

              {/* Tabela do Histórico */}
              <div className="admin-table-wrap" style={{ maxHeight: '380px', overflowY: 'auto', border: '1px solid var(--line)', borderRadius: '5px' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: '140px' }}>Data</th>
                      <th style={{ width: '100px' }}>Horário</th>
                      <th style={{ width: '260px' }}>Espaço</th>
                      <th style={{ width: '130px' }}>Evento</th>
                      <th>Detalhes operacionais</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalVisibleEvents.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '36px 0', color: 'var(--muted)' }}>
                          Nenhum registro encontrado para a busca selecionada.
                        </td>
                      </tr>
                    ) : (
                      modalVisibleEvents.map((evt, idx) => {
                        const isCriticalAttendance = evt.type === 'absence' || evt.type === 'delay'
                        const isToleranceWarning = evt.punctualityStatus === 'within_tolerance'
                        return (
                        <tr key={idx} style={{
                          backgroundColor: evt.type === 'spoof_attempt' || isCriticalAttendance
                            ? 'rgba(239, 68, 68, 0.04)'
                            : isToleranceWarning
                              ? 'rgba(249, 115, 22, 0.07)'
                              : undefined,
                          borderLeft: isCriticalAttendance ? '3px solid #ef4444' : isToleranceWarning ? '3px solid #f97316' : undefined,
                        }}>
                          <td>{evt.date}</td>
                          <td><strong>{evt.time}</strong></td>
                          <td>{evt.space}</td>
                          <td>{getEventBadge(evt.type)}</td>
                          <td style={{ fontSize: '0.82rem', color: evt.type === 'spoof_attempt' || isCriticalAttendance ? '#b91c1c' : isToleranceWarning ? '#c2410c' : 'var(--muted)', fontWeight: isCriticalAttendance || isToleranceWarning ? 700 : 500 }}>{evt.details}</td>
                        </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {modalTotalPages > 1 ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--line)', paddingTop: '16px' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', height: '32px' }}>
                    Exibindo página {modalPage} de {modalTotalPages}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="admin-tag"
                      disabled={modalPage === 1}
                      onClick={() => setModalPage((p) => Math.max(p - 1, 1))}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid var(--line)',
                        borderRadius: '4px',
                        backgroundColor: modalPage === 1 ? 'rgba(0,0,0,0.03)' : '#fff',
                        cursor: modalPage === 1 ? 'default' : 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: modalPage === 1 ? 'var(--muted)' : 'var(--text)'
                      }}
                    >
                      <ChevronLeftIcon />
                      Anterior
                    </button>
                    <button
                      type="button"
                      className="admin-tag"
                      disabled={modalPage === modalTotalPages}
                      onClick={() => setModalPage((p) => Math.min(p + 1, modalTotalPages))}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid var(--line)',
                        borderRadius: '4px',
                        backgroundColor: modalPage === modalTotalPages ? 'rgba(0,0,0,0.03)' : '#fff',
                        cursor: modalPage === modalTotalPages ? 'default' : 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: modalPage === modalTotalPages ? 'var(--muted)' : 'var(--text)'
                      }}
                    >
                      Próximo
                      <ChevronRightIcon />
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Ações / Fechar */}
              <div className="admin-modal__actions" style={{ borderTop: '1px solid var(--line)', paddingTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  className="admin-action-button"
                  onClick={closeCollaboratorHistory}
                  style={{ minWidth: '100px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '13px', height: '13px' }}>
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {editingProfile ? (
        <div className="admin-modal admin-modal--stacked" role="dialog" aria-modal="true" aria-label={`Editar perfil: ${editingProfile.name}`}>
          <div className="admin-modal__surface admin-modal__surface--view admin-profile-modal">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              aria-label={t('common.cancel')}
              onClick={closeProfileOverlay}
            >
              <XIcon />
            </button>
            <div className="admin-modal__view admin-profile-modal__body">
              <div className="admin-modal__view-hero admin-profile-modal__hero">
                <p className="admin-modal__eyebrow">Perfil do colaborador</p>
                <div>
                  <h3>{editingProfile.name}</h3>
                  <p>{editingProfile.email}</p>
                </div>
                <span className={`admin-status ${profileForm.status === 'active' ? 'admin-status--active' : 'admin-status--inactive'}`}>
                  {profileForm.status === 'active' ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <section className="admin-view-card admin-view-card--wide admin-profile-modal__card">
                <span>Edição do perfil</span>
                <div className="admin-profile-modal__form">
                  <label>
                    <span>Nome</span>
                    <input
                      type="text"
                      value={profileForm.name}
                      onChange={(event) => setProfileForm((current) => ({ ...current, name: event.target.value }))}
                    />
                  </label>
                  <label>
                    <span>E-mail</span>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(event) => setProfileForm((current) => ({ ...current, email: event.target.value }))}
                    />
                  </label>
                  <label>
                    <span>Status</span>
                    <div className="admin-status-switch" data-state={profileForm.status}>
                      <span className={`admin-status-switch__label${profileForm.status !== 'active' ? ' admin-status-switch__label--active' : ''}`}>
                        Inativo
                      </span>
                      <label className="admin-status-switch__control">
                        <input
                          type="checkbox"
                          checked={profileForm.status === 'active'}
                          onChange={(event) =>
                            setProfileForm((current) => ({
                              ...current,
                              status: event.target.checked ? 'active' : 'inactive',
                            }))
                          }
                          aria-label="Status do colaborador"
                        />
                        <span className="admin-status-switch__track">
                          <span className="admin-status-switch__thumb" />
                        </span>
                      </label>
                      <span className={`admin-status-switch__label${profileForm.status === 'active' ? ' admin-status-switch__label--active' : ''}`}>
                        Ativo
                      </span>
                    </div>
                  </label>
                  <label>
                    <span>Máximo de jornadas por dia</span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={profileForm.maxDailyJourneys}
                      onChange={(event) =>
                        setProfileForm((current) => ({
                          ...current,
                          maxDailyJourneys: Math.max(1, Math.floor(Number(event.target.value) || 1)),
                        }))
                      }
                    />
                    <small>Define quantas vezes o colaborador pode iniciar e encerrar uma jornada no mesmo dia.</small>
                  </label>
                </div>
                {profileFeedback ? (
                  <p className={`admin-profile-modal__feedback ${profileFeedback === 'Perfil atualizado.' ? 'admin-profile-modal__feedback--success' : 'admin-profile-modal__feedback--error'}`}>
                    {profileFeedback}
                  </p>
                ) : null}
              </section>
              <div className="admin-modal__actions admin-profile-modal__actions">
                <button type="button" className="admin-link-button" onClick={closeProfileOverlay}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="admin-action-button"
                  disabled={profileSaving}
                  onClick={() => void handleSaveCollaboratorProfile()}
                >
                  {profileSaving ? 'Salvando...' : 'Salvar perfil'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
